import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, User } from "lucide-react";
import PageHero from "@/components/PageHero";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";

const GoogleBtn = ({ onClick }) => (
  <button onClick={onClick} className="w-full flex items-center justify-center gap-3 border border-white/20 py-3 text-white/80 hover:border-gold hover:text-gold transition-colors" data-testid="google-login">
    <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.5 29.5 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.5 29.5 4.5 24 4.5 16.3 4.5 9.7 8.9 6.3 14.7z"/><path fill="#4CAF50" d="M24 43.5c5.4 0 10.3-2 14-5.3l-6.5-5.3c-2 1.5-4.6 2.4-7.5 2.4-5.2 0-9.6-3.3-11.2-7.9l-6.5 5C9.6 39 16.2 43.5 24 43.5z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.4l6.5 5.3c-.5.4 7-5.1 7-14.7 0-1.2-.1-2.3-.4-3.5z"/></svg>
    Continue with Google
  </button>
);

export default function Account() {
  const { user, login, register, loginGoogle, logout } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [hasPending, setHasPending] = useState(false);

  useEffect(() => {
    if (user && user.email) {
      api.get("/customer/payments").then((r) => setPayments(r.data)).catch(() => {});
      api.get("/customer/bookings").then((r) => setBookings(r.data)).catch(() => {});
      setHasPending(!!localStorage.getItem("ko_lastquote"));
    }
  }, [user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setLoading(true); setErr("");
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register({ name: form.name, email: form.email, password: form.password, phone: form.phone });
      toast.success("Welcome!");
    } catch (e2) {
      const d = e2.response?.data?.detail; setErr(typeof d === "string" ? d : "Something went wrong");
    } finally { setLoading(false); }
  };

  if (user && user.email) {
    return (
      <div data-testid="account-page">
        <PageHero overline="My Account" title={`Welcome, ${user.name || "Guest"}`} subtitle="Manage your bookings and payments."
          image="https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000" />
        <section className="py-20">
          <div className="container-lux max-w-3xl">
            {hasPending && (
              <div className="border border-gold/40 bg-gold/[0.05] p-6 mb-8 flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="pending-booking-banner">
                <p className="text-white/80 text-sm">You have a pending booking. Complete your payment to reserve your date.</p>
                <button onClick={() => nav("/request-a-quote")} className="btn-gold !py-2 !px-5 text-[11px]" data-testid="complete-payment-btn">Complete Payment</button>
              </div>
            )}
            <div className="border border-white/10 p-8 mb-8 flex items-center justify-between">
              <div className="flex items-center gap-4">
                {user.picture ? <img src={user.picture} alt="" className="w-12 h-12 rounded-full" /> : <div className="w-12 h-12 bg-gold/20 flex items-center justify-center"><User className="w-6 h-6 text-gold" /></div>}
                <div><p className="text-white">{user.name}</p><p className="text-white/50 text-sm">{user.email}</p></div>
              </div>
              <button onClick={() => { logout(); toast.success("Signed out"); }} className="flex items-center gap-2 text-white/60 hover:text-gold text-sm" data-testid="account-logout"><LogOut className="w-4 h-4" />Sign Out</button>
            </div>
            <h3 className="text-xl font-light text-white mb-4">My Bookings</h3>
            <div className="space-y-3 mb-10">
              {bookings.map((b) => {
                const confirmed = b.status.startsWith("Confirmed");
                const pending = b.status.includes("Balance Pending");
                return (
                  <div key={b.id} className="border border-white/10 p-5" data-testid={`booking-${b.id}`}>
                    <div className="flex justify-between items-start gap-4">
                      <div>
                        <p className="text-white">{b.event_type || "Event"} · <span className="text-white/50 text-sm">{b.event_date || "date TBD"} · {b.guests} guests</span></p>
                        <p className="text-white/45 text-xs mt-1">Total ${b.estimated_total?.toFixed(2)} · Paid ${b.paid?.toFixed(2)} · Balance <span className="text-gold">${b.balance?.toFixed(2)}</span></p>
                      </div>
                      <span className={`text-[10px] uppercase tracking-widest px-3 py-1 border shrink-0 ${confirmed ? "border-green-500 text-green-400" : pending ? "border-gold text-gold" : "border-white/25 text-white/50"}`}>{b.status}</span>
                    </div>
                    {b.balance > 0 && (
                      <button onClick={() => nav("/request-a-quote")} className="mt-3 text-gold text-xs uppercase tracking-widest" data-testid={`booking-pay-${b.id}`}>Pay Balance →</button>
                    )}
                  </div>
                );
              })}
              {!bookings.length && <p className="text-white/40">No bookings yet. Request a quote to get started.</p>}
            </div>

            <h3 className="text-xl font-light text-white mb-4">My Payments</h3>
            <div className="space-y-3">
              {payments.map((p) => (
                <div key={p.id} className="border border-white/10 p-5 flex justify-between items-center" data-testid={`my-payment-${p.id}`}>
                  <div><p className="text-white text-sm capitalize">{p.plan === "50" ? "50% Payment" : p.plan === "100" ? "Full Payment" : p.plan} · <span className="text-gold">${p.amount.toFixed(2)}</span></p><p className="text-white/40 text-xs">{p.event_type} · {new Date(p.created_at).toLocaleDateString()}</p></div>
                  <span className={`text-[10px] uppercase tracking-widest px-3 py-1 border ${p.status === "paid" ? "border-green-500 text-green-400" : "border-gold text-gold"}`}>{p.status}</span>
                </div>
              ))}
              {!payments.length && <p className="text-white/40">No payments yet. Request a quote to reserve your date.</p>}
            </div>
            <button onClick={() => nav("/request-a-quote")} className="btn-outline mt-8" data-testid="account-request-quote">Request a Quote</button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="account-auth-page">
      <Seo title="Sign In or Register | K.O Chef & Catering" description="Sign in or create your K.O Chef & Catering account to book and pay for your event." path="/account" noindex />
      <PageHero overline="Account" title="Sign In or Register" subtitle="Create an account to book and pay for your event."
        image="https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000" />
      <section className="py-16">
        <div className="container-lux max-w-md">
          <div className="flex border-b border-white/10 mb-8">
            <button onClick={() => setMode("login")} className={`flex-1 py-3 uppercase text-xs tracking-widest ${mode === "login" ? "text-gold border-b-2 border-gold" : "text-white/50"}`} data-testid="tab-login">Sign In</button>
            <button onClick={() => setMode("register")} className={`flex-1 py-3 uppercase text-xs tracking-widest ${mode === "register" ? "text-gold border-b-2 border-gold" : "text-white/50"}`} data-testid="tab-register">Register</button>
          </div>
          <form onSubmit={submit} className="space-y-6" data-testid="auth-form">
            {mode === "register" && <input required value={form.name} onChange={set("name")} placeholder="Full Name" className="lux-input" data-testid="auth-name" />}
            <input required type="email" value={form.email} onChange={set("email")} placeholder="Email" className="lux-input" data-testid="auth-email" />
            {mode === "register" && <input value={form.phone} onChange={set("phone")} placeholder="Phone (optional)" className="lux-input" data-testid="auth-phone" />}
            <input required type="password" value={form.password} onChange={set("password")} placeholder="Password" className="lux-input" data-testid="auth-password" />
            {err && <p className="text-red-400 text-sm" data-testid="auth-error">{err}</p>}
            <button disabled={loading} className="btn-gold w-full" data-testid="auth-submit">{loading ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"}</button>
          </form>
          <div className="flex items-center gap-4 my-6"><div className="flex-1 h-px bg-white/10" /><span className="text-white/30 text-xs uppercase tracking-widest">or</span><div className="flex-1 h-px bg-white/10" /></div>
          <GoogleBtn onClick={loginGoogle} />
        </div>
      </section>
    </div>
  );
}
